import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Calendar, Clock, MapPin, User, FileText, Save, Loader2, CheckCircle, Package, Camera, X, Send, AlertTriangle, Leaf, Phone, Receipt, Plus, Trash2, Pencil } from 'lucide-react'
import { format } from 'date-fns'
import { sv } from 'date-fns/locale'
import toast from 'react-hot-toast'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../lib/auth'
import { getAssignmentStatusBadgeClassesV2, ASSIGNMENT_STATUSES_V2 } from '../../services/statusSystemV2'
import type { AssignmentStatusV2 } from '../../types/statusTypes'
import { logAssignmentChange } from '../../services/activityLog'
import { usePageTracking } from '../../hooks/usePageTracking'
import { TgDeclineModal } from '../../components/dialogs/TgDeclineModal'
import { CommentSection } from '../../components/comments/CommentSection'
import { AttachmentSection } from '../../components/assignment'

interface ReportImage {
  url: string
  name: string
  uploaded_at: string
  type: 'before' | 'after' | 'other'
}

interface ReportedArticle {
  article_id: string | null  // null för utlägg
  article_name: string
  article_description?: string | null
  article_price?: number | null
  planned_quantity: number | null
  actual_quantity: number | null
  unit: string | null
  notes: string
  is_extra: boolean
  images: ReportImage[]
  // Utlägg/kvitto-fält
  is_expense?: boolean
  expense_amount_incl_vat?: number | null
  expense_amount_excl_vat?: number | null
  expense_vat_rate?: number | null
}

interface Coworker {
  id: string
  name: string
  phone: string | null
  color: string | null
}

interface AssignmentData {
  id: string
  title: string
  description: string | null
  status: string
  start_date: string | null
  start_time: string | null
  end_time: string | null
  assignment_number: number | null
  location: string | { address?: string } | null
  customer: {
    full_name: string
    phone: string | null
    hide_details_from_tg: boolean | null
  } | null
  articles: Array<{
    id: string
    quantity: number
    article: {
      id: string
      name: string
      description?: string | null
      unit: string | null
      price?: number | null
    }
  }>
  // Report data for this subcontractor
  subcontractorId: string
  subcontractorName: string
  reportText: string | null
  reportedAt: string | null
  adminFeedback: string | null
  reportedArticles: ReportedArticle[] | null
  reportImages: ReportImage[] | null
  isDraft: boolean
  draftSavedAt: string | null
  draftSaverName: string | null
  tgStatus: string | null
  coworkers: Coworker[]
}

export function SubcontractorAssignmentDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const [saving, setSaving] = useState(false)

  // Track page view with assignment ID
  usePageTracking('assignment_detail', id)
  const [submitting, setSubmitting] = useState(false)
  const [report, setReport] = useState('')
  const [articleReports, setArticleReports] = useState<ReportedArticle[]>([])
  const [reportImages, setReportImages] = useState<ReportImage[]>([])
  const [uploadingGeneral, setUploadingGeneral] = useState(false)
  const [uploadingArticle, setUploadingArticle] = useState<string | null>(null)
  const beforeGeneralInputRef = useRef<HTMLInputElement>(null)
  const afterGeneralInputRef = useRef<HTMLInputElement>(null)
  const otherGeneralInputRef = useRef<HTMLInputElement>(null)
  const beforeArticleInputRef = useRef<HTMLInputElement>(null)
  const afterArticleInputRef = useRef<HTMLInputElement>(null)
  const otherArticleInputRef = useRef<HTMLInputElement>(null)
  const [activeArticleUpload, setActiveArticleUpload] = useState<number | null>(null)
  const [activeUploadType, setActiveUploadType] = useState<'before' | 'after' | 'other'>('other')
  const [showDeclineModal, setShowDeclineModal] = useState(false)
  const [showSubmitConfirmation, setShowSubmitConfirmation] = useState(false)
  const [selectedCoworker, setSelectedCoworker] = useState<Coworker | null>(null)

  // Expense (utlägg) state
  const [expenses, setExpenses] = useState<ReportedArticle[]>([])
  const [showExpenseModal, setShowExpenseModal] = useState(false)
  const [editingExpenseIndex, setEditingExpenseIndex] = useState<number | null>(null)
  const [newExpense, setNewExpense] = useState({
    description: '',
    amountExclVat: '',
    amountInclVat: '',
    vatRate: 25,
    lastEdited: 'excl' as 'excl' | 'incl',
    images: [] as ReportImage[]
  })
  const [uploadingExpenseImage, setUploadingExpenseImage] = useState(false)
  const expenseImageInputRef = useRef<HTMLInputElement>(null)

  // Track saved state for unsaved changes detection
  const [savedState, setSavedState] = useState<{
    report: string
    articleReports: string
    reportImages: string
    expenses: string
  } | null>(null)
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null)

  // Fetch assignment data
  const { data: assignment, isLoading, error } = useQuery<AssignmentData | null>({
    queryKey: ['subcontractor-assignment', id],
    queryFn: async () => {
      if (!id || !user?.id) return null

      // First get the subcontractor ID for this user
      const { data: subData } = await supabase
        .from('subcontractors')
        .select('id, name')
        .eq('user_id', user.id)
        .single()

      if (!subData) {
        throw new Error('Kunde inte hitta din trädgårdsmästarprofil')
      }

      // Check that this subcontractor is assigned to this assignment and get report data
      const { data: linkData } = await supabase
        .from('assignment_subcontractors')
        .select(`
          id, report_text, reported_at, admin_feedback,
          reported_articles, report_images, is_draft, tg_status,
          draft_saved_at, draft_saved_by
        `)
        .eq('assignment_id', id)
        .eq('subcontractor_id', subData.id)
        .single()

      if (!linkData) {
        throw new Error('Du är inte tilldelad detta uppdrag')
      }

      // Check if TG has declined this assignment
      if (linkData.tg_status === 'avsagt') {
        throw new Error('Du har avsagt dig detta uppdrag')
      }

      // Fetch the assignment
      const { data, error } = await supabase
        .from('assignments')
        .select(`
          id,
          title,
          description,
          status,
          start_date,
          start_time,
          end_time,
          assignment_number,
          location,
          customers (
            full_name,
            phone,
            hide_details_from_tg
          )
        `)
        .eq('id', id)
        .single()

      if (error) throw error

      // Fetch articles using RPC function (bypasses RLS so subcontractors can see article names)
      const { data: articlesData } = await supabase.rpc('get_articles_for_assignment', {
        p_assignment_id: id,
        p_subcontractor_id: subData.id
      })

      // Fetch coworkers (other TGs on this assignment, exclude declined)
      const { data: coworkerData } = await supabase
        .from('assignment_subcontractors')
        .select(`
          subcontractors (id, name, phone, color)
        `)
        .eq('assignment_id', id)
        .neq('subcontractor_id', subData.id)
        .neq('tg_status', 'avsagt')

      const coworkers: Coworker[] = (coworkerData || [])
        .map(row => {
          const sub = row.subcontractors as any
          return sub ? {
            id: sub.id,
            name: sub.name,
            phone: sub.phone,
            color: sub.color
          } : null
        })
        .filter(Boolean) as Coworker[]

      return {
        ...data,
        customer: data.customers as any,
        // Use articles from RPC function, filter out those excluded from TG reporting
        articles: (articlesData || [])
          .filter((a: any) => !a.exclude_from_tg_reporting)
          .map((a: any) => ({
            id: a.assignment_article_id,
            quantity: a.quantity,
            article: {
              id: a.article_id,
              name: a.article_name,
              description: a.article_description,
              unit: a.article_unit,
              price: a.article_price
            }
          })),
        subcontractorId: subData.id,
        subcontractorName: subData.name || 'Trädgårdsmästare',
        reportText: linkData.report_text,
        reportedAt: linkData.reported_at,
        adminFeedback: linkData.admin_feedback,
        reportedArticles: linkData.reported_articles as ReportedArticle[] | null,
        reportImages: linkData.report_images as ReportImage[] | null,
        isDraft: linkData.is_draft || false,
        draftSavedAt: linkData.draft_saved_at,
        draftSaverName: linkData.draft_saved_by === user?.id ? 'dig' : (linkData.draft_saved_by ? 'admin' : null),
        tgStatus: linkData.tg_status,
        coworkers
      } as AssignmentData
    },
    enabled: !!id && !!user?.id
  })


  // Initialize report fields from loaded data
  useEffect(() => {
    if (assignment) {
      const reportText = assignment.reportText || ''
      const images = assignment.reportImages || []

      setReport(reportText)
      setReportImages(images)

      // Initialize article reports and expenses
      let articles: ReportedArticle[]
      let loadedExpenses: ReportedArticle[] = []

      if (assignment.reportedArticles && assignment.reportedArticles.length > 0) {
        // Separate expenses from regular articles
        articles = assignment.reportedArticles.filter(a => !a.is_expense)
        loadedExpenses = assignment.reportedArticles.filter(a => a.is_expense)
      } else {
        articles = assignment.articles.map(a => ({
          article_id: a.article.id,
          article_name: a.article.name,
          article_description: a.article.description,
          article_price: a.article.price,
          planned_quantity: a.quantity,
          actual_quantity: null,
          unit: a.article.unit,
          notes: '',
          is_extra: false,
          images: []
        }))
      }
      setArticleReports(articles)
      setExpenses(loadedExpenses)

      // Save initial state for unsaved changes detection
      setSavedState({
        report: reportText,
        articleReports: JSON.stringify(articles),
        reportImages: JSON.stringify(images),
        expenses: JSON.stringify(loadedExpenses)
      })
    }
  }, [assignment])

  // Upload image to Supabase Storage
  const uploadImage = async (file: File, folder: string, type: 'before' | 'after' | 'other'): Promise<ReportImage | null> => {
    try {
      const fileExt = file.name.split('.').pop()
      const fileName = `${id}/${folder}/${Date.now()}.${fileExt}`

      const { error: uploadError } = await supabase.storage
        .from('report-images')
        .upload(fileName, file)

      if (uploadError) throw uploadError

      const { data: urlData } = supabase.storage
        .from('report-images')
        .getPublicUrl(fileName)

      return {
        url: urlData.publicUrl,
        name: file.name,
        uploaded_at: new Date().toISOString(),
        type
      }
    } catch (err) {
      console.error('Upload error:', err)
      return null
    }
  }

  // Handle general report image upload
  const handleGeneralImageUpload = async (files: FileList | null, type: 'before' | 'after' | 'other') => {
    if (!files || files.length === 0) return

    setUploadingGeneral(true)
    const newImages: ReportImage[] = []

    for (const file of Array.from(files)) {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} är inte en bild`)
        continue
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} är för stor (max 10MB)`)
        continue
      }
      const uploaded = await uploadImage(file, 'general', type)
      if (uploaded) newImages.push(uploaded)
    }

    if (newImages.length > 0) {
      setReportImages([...reportImages, ...newImages])
      toast.success(`${newImages.length} bild(er) uppladdade`)
    }
    setUploadingGeneral(false)
  }

  // Handle expense image upload
  const handleExpenseImageUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return

    setUploadingExpenseImage(true)
    const newImages: ReportImage[] = []

    for (const file of Array.from(files)) {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} är inte en bild`)
        continue
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} är för stor (max 10MB)`)
        continue
      }
      const uploaded = await uploadImage(file, 'expense', 'other')
      if (uploaded) newImages.push(uploaded)
    }

    if (newImages.length > 0) {
      setNewExpense(prev => ({
        ...prev,
        images: [...prev.images, ...newImages]
      }))
      toast.success(`${newImages.length} bild(er) uppladdade`)
    }
    setUploadingExpenseImage(false)
  }

  // Handle article-specific image upload
  const handleArticleImageUpload = async (files: FileList | null, articleIndex: number, type: 'before' | 'after' | 'other') => {
    if (!files || files.length === 0) return

    const article = articleReports[articleIndex]
    setUploadingArticle(article.article_id)
    const newImages: ReportImage[] = []

    for (const file of Array.from(files)) {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} är inte en bild`)
        continue
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} är för stor (max 10MB)`)
        continue
      }
      const uploaded = await uploadImage(file, `article-${article.article_id}`, type)
      if (uploaded) newImages.push(uploaded)
    }

    if (newImages.length > 0) {
      const newReports = [...articleReports]
      newReports[articleIndex] = {
        ...newReports[articleIndex],
        images: [...(newReports[articleIndex].images || []), ...newImages]
      }
      setArticleReports(newReports)
      toast.success(`${newImages.length} bild(er) uppladdade`)
    }
    setUploadingArticle(null)
    setActiveArticleUpload(null)
  }

  const removeGeneralImage = (index: number) => {
    setReportImages(reportImages.filter((_, i) => i !== index))
  }

  const removeArticleImage = (articleIndex: number, imageIndex: number) => {
    const newReports = [...articleReports]
    newReports[articleIndex] = {
      ...newReports[articleIndex],
      images: newReports[articleIndex].images.filter((_, i) => i !== imageIndex)
    }
    setArticleReports(newReports)
  }

  // Handle declining the assignment
  const handleDecline = async (reason: string) => {
    if (!id || !assignment) {
      toast.error('Kunde inte avsäga uppdraget')
      return
    }

    try {
      // Update assignment_subcontractors with declined status and reason
      const { error: updateError } = await supabase
        .from('assignment_subcontractors')
        .update({
          tg_status: 'avsagt',
          declined_at: new Date().toISOString(),
          declined_reason: reason
        })
        .eq('assignment_id', id)
        .eq('subcontractor_id', assignment.subcontractorId)

      if (updateError) throw updateError

      // Log the decline in activity log
      await logAssignmentChange(
        id,
        'tg_declined',
        { description: `${assignment.subcontractorName} avsade uppdraget. Anledning: ${reason}` }
      )

      // Delete notifications for this assignment for this user
      await supabase
        .from('notifications')
        .delete()
        .like('link', `%/assignments/${id}%`)

      // SMS to admins is handled by database trigger (notify_admins_on_tg_decline)

      toast.success('Du har avsagt dig uppdraget')
      queryClient.invalidateQueries({ queryKey: ['subcontractor-assignment', id] })
      queryClient.invalidateQueries({ queryKey: ['subcontractor-assignments'] })
      navigate('/subcontractor/assignments')
    } catch (err) {
      console.error('Error declining assignment:', err)
      toast.error('Kunde inte avsäga uppdraget')
    }
  }

  // Check if assignment can be declined (only before work starts)
  const canDecline = assignment && ['planerad', 'preliminar'].includes(assignment.status)

  // Check if there are unsaved changes
  const hasUnsavedChanges = savedState ? (
    report !== savedState.report ||
    JSON.stringify(articleReports) !== savedState.articleReports ||
    JSON.stringify(reportImages) !== savedState.reportImages ||
    JSON.stringify(expenses) !== savedState.expenses
  ) : false

  // Check if there's something to submit
  const hasReportContent = articleReports.some(a => a.actual_quantity !== null && a.actual_quantity > 0) || expenses.length > 0 || report.trim().length > 0

  // Save as draft (without submitting for approval)
  const handleSaveDraft = async () => {
    if (!id || !assignment) {
      toast.error('Kunde inte spara')
      return
    }

    try {
      setSaving(true)

      // Combine articles and expenses into reported_articles
      const allReportedArticles = [...articleReports, ...expenses]

      // Save draft to assignment_subcontractors
      const { error: saveError } = await supabase
        .from('assignment_subcontractors')
        .update({
          report_text: report || null,
          reported_articles: allReportedArticles,
          report_images: reportImages,
          is_draft: true,
          draft_saved_at: new Date().toISOString(),
          draft_saved_by: user?.id
        })
        .eq('assignment_id', id)
        .eq('subcontractor_id', assignment.subcontractorId)

      if (saveError) throw saveError

      // Update saved state and timestamp
      setSavedState({
        report,
        articleReports: JSON.stringify(articleReports),
        reportImages: JSON.stringify(reportImages),
        expenses: JSON.stringify(expenses)
      })
      setLastSavedAt(new Date())

      toast.success('Utkast sparat!')
      queryClient.invalidateQueries({ queryKey: ['subcontractor-assignment', id] })
    } catch (err) {
      console.error('Error saving draft:', err)
      toast.error('Kunde inte spara utkast')
    } finally {
      setSaving(false)
    }
  }

  // Submit report for approval
  const handleSubmitReport = async () => {
    if (!id || !assignment) {
      toast.error('Kunde inte skicka rapport')
      return
    }

    // Check that at least one article has been reported, there's an expense, or there's a text report
    const hasArticleReports = articleReports.some(a => a.actual_quantity !== null && a.actual_quantity > 0)
    if (!hasArticleReports && expenses.length === 0 && !report.trim()) {
      toast.error('Rapportera minst en artikel, lägg till ett utlägg, eller skriv en anteckning')
      return
    }

    try {
      setSubmitting(true)

      const now = new Date().toISOString()

      // Combine articles and expenses into reported_articles
      const allReportedArticles = [...articleReports, ...expenses]

      // Save report to assignment_subcontractors and update TG status to 'rapporterad'
      const { error: reportError } = await supabase
        .from('assignment_subcontractors')
        .update({
          report_text: report || null,
          reported_at: now,
          reported_articles: allReportedArticles,
          report_images: reportImages,
          is_draft: false,
          tg_status: 'rapporterad'  // V2: Update TG work status
        })
        .eq('assignment_id', id)
        .eq('subcontractor_id', assignment.subcontractorId)

      if (reportError) throw reportError

      // Check if ALL subcontractors have now reported
      const { data: allLinks } = await supabase
        .from('assignment_subcontractors')
        .select('id, reported_at')
        .eq('assignment_id', id)

      const allReported = allLinks && allLinks.length > 0 && allLinks.every(link => link.reported_at)

      // If all have reported, change status to 'granskning' (V2)
      if (allReported) {
        const { error: statusError } = await supabase
          .from('assignments')
          .update({
            status: 'granskning',
            updated_at: now
          })
          .eq('id', id)

        if (statusError) {
          console.error('Error updating status:', statusError)
        } else {
          // Log status change
          await logAssignmentChange(id, 'status_changed', {
            fieldName: 'status',
            newValue: 'Granskning',
            description: 'Alla trädgårdsmästare har rapporterat'
          })
        }
      }

      // Log activity
      const reportedArticlesSummary = articleReports
        .filter(a => a.actual_quantity !== null && a.actual_quantity > 0)
        .map(a => `${a.article_name}: ${a.actual_quantity} ${a.unit || 'st'}`)
        .join(', ')

      await logAssignmentChange(id, 'report_submitted', {
        description: `${assignment.subcontractorName} skickade in rapport${reportedArticlesSummary ? `: ${reportedArticlesSummary}` : ''}`
      })

      toast.success('Rapport skickad för godkännande!')
      queryClient.invalidateQueries({ queryKey: ['subcontractor-assignment', id] })
    } catch (err) {
      console.error('Error submitting report:', err)
      toast.error('Kunde inte skicka rapport')
    } finally {
      setSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-nature-600 dark:text-nature-400">Laddar uppdrag...</p>
        </div>
      </div>
    )
  }

  if (error || !assignment) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 dark:text-red-400 mb-4">
          {error instanceof Error ? error.message : 'Kunde inte ladda uppdraget'}
        </p>
        <button
          onClick={() => navigate('/subcontractor/assignments')}
          className="btn-primary"
        >
          Tillbaka till uppdrag
        </button>
      </div>
    )
  }

  const fullAddress = typeof assignment.location === 'string'
    ? assignment.location
    : (assignment.location as any)?.address || ''

  const mapsUrl = fullAddress
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`
    : ''

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700 p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/subcontractor/assignments')}
              className="p-2 rounded-lg hover:bg-nature-100 dark:hover:bg-nature-700 transition-colors flex-shrink-0"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                {assignment.assignment_number && (
                  <span className="text-primary-600 dark:text-primary-400 font-mono">
                    #{assignment.assignment_number}
                  </span>
                )}
                <h1 className="text-xl font-bold text-nature-900 dark:text-nature-100 break-words">
                  {assignment.title || 'Namnlöst uppdrag'}
                </h1>
              </div>
              <span className={`inline-flex mt-1 px-2 py-0.5 text-xs font-medium rounded-full ${getAssignmentStatusBadgeClassesV2(assignment.status as AssignmentStatusV2)}`}>
                {ASSIGNMENT_STATUSES_V2.find(s => s.key === assignment.status)?.name || assignment.status}
              </span>
            </div>
          </div>
          {/* Decline button - only show before work starts */}
          {canDecline && (
            <button
              onClick={() => setShowDeclineModal(true)}
              className="flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors sm:flex-shrink-0 w-full sm:w-auto"
            >
              <AlertTriangle className="h-4 w-4" />
              Avsäg uppdrag
            </button>
          )}
        </div>
      </div>

      {/* Assignment Info */}
      <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700 p-6">
        <h2 className="text-lg font-semibold text-nature-900 dark:text-nature-100 mb-4">Uppdragsinformation</h2>

        <div className="space-y-4">
          {/* Date and Time */}
          <div className="flex items-start gap-3">
            <Calendar className="h-5 w-5 text-nature-500 mt-0.5" />
            <div>
              <p className="font-medium text-nature-900 dark:text-nature-100">
                {assignment.start_date
                  ? format(new Date(assignment.start_date), 'EEEE d MMMM yyyy', { locale: sv })
                  : 'Datum ej satt'}
              </p>
              {assignment.start_time && (
                <p className="text-sm text-nature-600 dark:text-nature-400 flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  {assignment.start_time.substring(0, 5)}
                  {assignment.end_time && ` - ${assignment.end_time.substring(0, 5)}`}
                </p>
              )}
            </div>
          </div>

          {/* Customer - only show if not hidden */}
          {!assignment.customer?.hide_details_from_tg && (
            <div className="flex items-start gap-3">
              <User className="h-5 w-5 text-nature-500 mt-0.5" />
              <div>
                <p className="font-medium text-nature-900 dark:text-nature-100">
                  {assignment.customer?.full_name || 'Okänd kund'}
                </p>
                {assignment.customer?.phone && (
                  <a
                    href={`tel:${assignment.customer.phone}`}
                    className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    {assignment.customer.phone}
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Address */}
          {fullAddress && (
            <div className="flex items-start gap-3">
              <MapPin className="h-5 w-5 text-nature-500 mt-0.5" />
              <div>
                <p className="font-medium text-nature-900 dark:text-nature-100">{fullAddress}</p>
                {mapsUrl && (
                  <a
                    href={mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    Öppna i Google Maps
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Description */}
          {assignment.description && (
            <div className="flex items-start gap-3">
              <FileText className="h-5 w-5 text-nature-500 mt-0.5" />
              <div>
                <p className="font-medium text-nature-900 dark:text-nature-100 mb-1">Beskrivning</p>
                <p className="text-nature-600 dark:text-nature-400 whitespace-pre-wrap">
                  {assignment.description}
                </p>
              </div>
            </div>
          )}

          {/* Coworkers */}
          {assignment.coworkers.length > 0 && (
            <div className="flex items-start gap-3">
              <Leaf className="h-5 w-5 text-nature-500 mt-0.5" />
              <div>
                <p className="font-medium text-nature-900 dark:text-nature-100 mb-2">Kollegor på uppdraget</p>
                <div className="flex flex-wrap gap-2">
                  {assignment.coworkers.map(coworker => (
                    <button
                      key={coworker.id}
                      onClick={() => setSelectedCoworker(coworker)}
                      className="px-3 py-1 text-sm rounded-full hover:opacity-80 transition-opacity"
                      style={{
                        backgroundColor: coworker.color ? `${coworker.color}20` : '#6B728020',
                        color: coworker.color || '#6B7280'
                      }}
                    >
                      {coworker.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Articles */}
      {assignment.articles.length > 0 && (
        <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700 p-6">
          <h2 className="text-lg font-semibold text-nature-900 dark:text-nature-100 mb-4">Artiklar</h2>
          <div className="space-y-3">
            {assignment.articles.map((item) => (
              <div
                key={item.id}
                className="py-2 border-b border-nature-100 dark:border-nature-700 last:border-0"
              >
                <div className="flex items-center justify-between">
                  <span className="text-nature-900 dark:text-nature-100 font-medium">{item.article.name}</span>
                  <div className="text-right">
                    <span className="text-nature-600 dark:text-nature-400">
                      {item.quantity} {item.article.unit || 'st'}
                    </span>
                    {item.article.price && (
                      <span className="ml-2 text-primary-600 dark:text-primary-400 font-medium">
                        {item.article.price} kr/{item.article.unit || 'st'}
                      </span>
                    )}
                  </div>
                </div>
                {item.article.description && (
                  <p className="text-sm text-nature-500 dark:text-nature-400 mt-1">
                    {item.article.description}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Report Section */}
      <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700 p-6">
        <h2 className="text-lg font-semibold text-nature-900 dark:text-nature-100 mb-4 flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-green-600" />
          Rapportera utfört arbete
        </h2>

        {/* Show if already reported (not in komplettering mode) */}
        {assignment.reportedAt && assignment.tgStatus !== 'komplettering' && (
          <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-green-800 dark:text-green-200">
                  Rapport inskickad
                </p>
                <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                  Din rapport skickades in {format(new Date(assignment.reportedAt), "d MMMM yyyy 'kl' HH:mm", { locale: sv })}.
                  Om du behöver göra ändringar, kontakta Malin.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Show admin feedback if any (komplettering required) */}
        {assignment.adminFeedback && assignment.tgStatus === 'komplettering' && (
          <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            <p className="text-sm font-medium text-amber-800 dark:text-amber-200 mb-1">
              Komplettering krävs
            </p>
            <p className="text-sm text-amber-700 dark:text-amber-300">
              {assignment.adminFeedback.replace(/^\[(BEGÄRD|MANUELL)\]\s*/, '')}
            </p>
          </div>
        )}

        <div className="space-y-6">
          {/* Article Reporting */}
          {articleReports.length > 0 && (
            <div>
              <h3 className="text-md font-medium text-nature-900 dark:text-nature-100 mb-3 flex items-center gap-2">
                <Package className="h-4 w-4" />
                Artiklar
              </h3>
              <div className="space-y-3">
                {articleReports.map((article, index) => (
                  <div
                    key={`${article.article_id}-${index}`}
                    className={`p-4 rounded-lg border ${
                      article.is_extra
                        ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                        : 'bg-nature-50 dark:bg-nature-700/50 border-nature-200 dark:border-nature-600'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-nature-900 dark:text-nature-100">
                            {article.article_name}
                          </span>
                          {article.article_price && (
                            <span className="text-sm text-primary-600 dark:text-primary-400 font-medium">
                              {article.article_price} kr/{article.unit || 'st'}
                            </span>
                          )}
                          {article.is_extra && (
                            <span className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-200 rounded-full">
                              Extra
                            </span>
                          )}
                        </div>
                        {article.article_description && (
                          <p className="text-sm text-nature-500 dark:text-nature-400 mt-1">
                            {article.article_description}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <div className="flex items-center gap-1">
                          <input
                            type="text"
                            inputMode="decimal"
                            defaultValue={article.actual_quantity !== null ? String(article.actual_quantity).replace('.', ',') : ''}
                            key={`${article.article_id}-${article.actual_quantity}`}
                            onBlur={(e) => {
                              const input = e.target.value.replace(',', '.')
                              if (input === '') {
                                const newReports = [...articleReports]
                                newReports[index] = { ...newReports[index], actual_quantity: null }
                                setArticleReports(newReports)
                              } else {
                                const parsed = parseFloat(input)
                                if (!isNaN(parsed)) {
                                  const newReports = [...articleReports]
                                  newReports[index] = { ...newReports[index], actual_quantity: parsed }
                                  setArticleReports(newReports)
                                }
                              }
                            }}
                            disabled={!!assignment.reportedAt && assignment.tgStatus !== 'komplettering'}
                            className="input-field w-20 text-center disabled:bg-nature-100 dark:disabled:bg-nature-700 disabled:cursor-not-allowed"
                            placeholder="0"
                          />
                          <span className="text-sm text-nature-600 dark:text-nature-400 w-12">
                            {article.unit || 'st'}
                          </span>
                        </div>
                        {article.planned_quantity !== null && (!assignment.reportedAt || assignment.tgStatus === 'komplettering') && (
                          <>
                            <button
                              type="button"
                              onClick={() => {
                                const newReports = [...articleReports]
                                newReports[index] = {
                                  ...newReports[index],
                                  actual_quantity: article.planned_quantity
                                }
                                setArticleReports(newReports)
                              }}
                              className="text-xs px-2 py-1 bg-blue-100 dark:bg-blue-800 rounded text-blue-700 dark:text-blue-200 hover:bg-blue-200 dark:hover:bg-blue-700"
                            >
                              Använd planerat ({article.planned_quantity})
                            </button>
                            <span className="text-xs text-nature-400 dark:text-nature-500 italic">
                              Planerat: {article.planned_quantity} {article.unit || 'st'}. Ange verkligt antal.
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="mt-2">
                      <input
                        type="text"
                        value={article.notes}
                        onChange={(e) => {
                          const newReports = [...articleReports]
                          newReports[index] = {
                            ...newReports[index],
                            notes: e.target.value
                          }
                          setArticleReports(newReports)
                        }}
                        disabled={!!assignment.reportedAt && assignment.tgStatus !== 'komplettering'}
                        className="input-field text-sm disabled:bg-nature-100 dark:disabled:bg-nature-700 disabled:cursor-not-allowed"
                        placeholder="Anteckning (valfritt)..."
                      />
                    </div>

                    {/* Article images */}
                    <div className="mt-3 space-y-2">
                      {/* Before images */}
                      {article.images && article.images.filter(img => img.type === 'before').length > 0 && (
                        <div>
                          <p className="text-xs text-nature-500 dark:text-nature-400 mb-1">Före:</p>
                          <div className="flex flex-wrap gap-2">
                            {article.images.filter(img => img.type === 'before').map((img, imgIdx) => {
                              const actualIdx = article.images.findIndex(i => i === img)
                              return (
                                <div key={imgIdx} className="relative group">
                                  <img
                                    src={img.url}
                                    alt={img.name}
                                    className="w-14 h-14 object-cover rounded border-2 border-amber-400 dark:border-amber-500"
                                  />
                                  {(!assignment.reportedAt || assignment.tgStatus === 'komplettering') && (
                                    <button
                                      onClick={() => removeArticleImage(index, actualIdx)}
                                      className="absolute -top-1 -right-1 p-0.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  )}
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      )}
                      {/* After images */}
                      {article.images && article.images.filter(img => img.type === 'after').length > 0 && (
                        <div>
                          <p className="text-xs text-nature-500 dark:text-nature-400 mb-1">Efter:</p>
                          <div className="flex flex-wrap gap-2">
                            {article.images.filter(img => img.type === 'after').map((img, imgIdx) => {
                              const actualIdx = article.images.findIndex(i => i === img)
                              return (
                                <div key={imgIdx} className="relative group">
                                  <img
                                    src={img.url}
                                    alt={img.name}
                                    className="w-14 h-14 object-cover rounded border-2 border-green-400 dark:border-green-500"
                                  />
                                  {(!assignment.reportedAt || assignment.tgStatus === 'komplettering') && (
                                    <button
                                      onClick={() => removeArticleImage(index, actualIdx)}
                                      className="absolute -top-1 -right-1 p-0.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  )}
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      )}
                      {/* Other images */}
                      {article.images && article.images.filter(img => img.type === 'other').length > 0 && (
                        <div>
                          <p className="text-xs text-nature-500 dark:text-nature-400 mb-1">Övriga:</p>
                          <div className="flex flex-wrap gap-2">
                            {article.images.filter(img => img.type === 'other').map((img, imgIdx) => {
                              const actualIdx = article.images.findIndex(i => i === img)
                              return (
                                <div key={imgIdx} className="relative group">
                                  <img
                                    src={img.url}
                                    alt={img.name}
                                    className="w-14 h-14 object-cover rounded border border-nature-300 dark:border-nature-500"
                                  />
                                  {(!assignment.reportedAt || assignment.tgStatus === 'komplettering') && (
                                    <button
                                      onClick={() => removeArticleImage(index, actualIdx)}
                                      className="absolute -top-1 -right-1 p-0.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  )}
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      )}
                      {/* Upload buttons - only show when not submitted or when supplement needed */}
                      {(!assignment.reportedAt || assignment.tgStatus === 'komplettering') && (
                        <div className="flex gap-2 flex-wrap">
                          <button
                            onClick={() => {
                              setActiveArticleUpload(index)
                              setActiveUploadType('before')
                              beforeArticleInputRef.current?.click()
                            }}
                            disabled={uploadingArticle === article.article_id}
                            className="text-xs px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded hover:bg-amber-200 dark:hover:bg-amber-900/50 flex items-center gap-1"
                          >
                            {uploadingArticle === article.article_id && activeUploadType === 'before' ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Camera className="h-3 w-3" />
                            )}
                            Före-bild
                          </button>
                          <button
                            onClick={() => {
                              setActiveArticleUpload(index)
                              setActiveUploadType('after')
                              afterArticleInputRef.current?.click()
                            }}
                            disabled={uploadingArticle === article.article_id}
                            className="text-xs px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded hover:bg-green-200 dark:hover:bg-green-900/50 flex items-center gap-1"
                          >
                            {uploadingArticle === article.article_id && activeUploadType === 'after' ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Camera className="h-3 w-3" />
                            )}
                            Efter-bild
                          </button>
                          <button
                            onClick={() => {
                              setActiveArticleUpload(index)
                              setActiveUploadType('other')
                              otherArticleInputRef.current?.click()
                            }}
                            disabled={uploadingArticle === article.article_id}
                            className="text-xs px-2 py-1 bg-nature-100 dark:bg-nature-700 text-nature-700 dark:text-nature-300 rounded hover:bg-nature-200 dark:hover:bg-nature-600 flex items-center gap-1"
                          >
                            {uploadingArticle === article.article_id && activeUploadType === 'other' ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Camera className="h-3 w-3" />
                            )}
                            Övrig bild
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Hidden inputs for article images */}
              <input
                ref={beforeArticleInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => {
                  if (activeArticleUpload !== null) {
                    handleArticleImageUpload(e.target.files, activeArticleUpload, 'before')
                  }
                  e.target.value = ''
                }}
                className="hidden"
              />
              <input
                ref={afterArticleInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => {
                  if (activeArticleUpload !== null) {
                    handleArticleImageUpload(e.target.files, activeArticleUpload, 'after')
                  }
                  e.target.value = ''
                }}
                className="hidden"
              />
              <input
                ref={otherArticleInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => {
                  if (activeArticleUpload !== null) {
                    handleArticleImageUpload(e.target.files, activeArticleUpload, 'other')
                  }
                  e.target.value = ''
                }}
                className="hidden"
              />
            </div>
          )}

          {/* Expenses (Utlägg) Section */}
          {(!assignment.reportedAt || assignment.tgStatus === 'komplettering') && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-md font-medium text-nature-900 dark:text-nature-100 flex items-center gap-2">
                  <Receipt className="h-4 w-4" />
                  Utlägg & Kvitton
                </h3>
                <button
                  onClick={() => setShowExpenseModal(true)}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-lg hover:bg-primary-200 dark:hover:bg-primary-900/50 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Lägg till
                </button>
              </div>

              {expenses.length === 0 ? (
                <p className="text-sm text-nature-500 dark:text-nature-400 italic">
                  Inga utlägg tillagda. Klicka på "Lägg till" för att registrera parkering, material eller andra utgifter.
                </p>
              ) : (
                <div className="space-y-2">
                  {expenses.map((expense, index) => (
                    <div
                      key={index}
                      className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Receipt className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                            <span className="font-medium text-nature-900 dark:text-nature-100">
                              {expense.article_name}
                            </span>
                          </div>
                          <div className="mt-1 text-sm text-nature-600 dark:text-nature-400">
                            <span className="font-medium">{expense.expense_amount_incl_vat?.toFixed(2)} kr</span>
                            <span className="text-nature-500"> inkl {expense.expense_vat_rate}% moms</span>
                            <span className="mx-2">•</span>
                            <span>{expense.expense_amount_excl_vat?.toFixed(2)} kr ex moms</span>
                          </div>
                          {expense.images && expense.images.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {expense.images.map((img, imgIdx) => (
                                <a
                                  key={imgIdx}
                                  href={img.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
                                >
                                  <Camera className="h-3 w-3" />
                                  Kvitto {imgIdx + 1}
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => {
                              setEditingExpenseIndex(index)
                              setNewExpense({
                                description: expense.article_name,
                                amountExclVat: String(expense.expense_amount_excl_vat || ''),
                                amountInclVat: String(expense.expense_amount_incl_vat || ''),
                                vatRate: expense.expense_vat_rate || 25,
                                lastEdited: 'excl',
                                images: expense.images || []
                              })
                              setShowExpenseModal(true)
                            }}
                            className="p-1.5 text-primary-600 hover:bg-primary-100 dark:hover:bg-primary-900/30 rounded transition-colors"
                            title="Redigera utlägg"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {
                              setExpenses(expenses.filter((_, i) => i !== index))
                            }}
                            className="p-1.5 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors"
                            title="Ta bort utlägg"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Show existing expenses when report is submitted */}
          {assignment.reportedAt && assignment.tgStatus !== 'komplettering' && expenses.length > 0 && (
            <div>
              <h3 className="text-md font-medium text-nature-900 dark:text-nature-100 mb-3 flex items-center gap-2">
                <Receipt className="h-4 w-4" />
                Utlägg & Kvitton
              </h3>
              <div className="space-y-2">
                {expenses.map((expense, index) => (
                  <div
                    key={index}
                    className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg"
                  >
                    <div className="flex items-center gap-2">
                      <Receipt className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                      <span className="font-medium text-nature-900 dark:text-nature-100">
                        {expense.article_name}
                      </span>
                    </div>
                    <div className="mt-1 text-sm text-nature-600 dark:text-nature-400">
                      <span className="font-medium">{expense.expense_amount_incl_vat?.toFixed(2)} kr</span>
                      <span className="text-nature-500"> inkl {expense.expense_vat_rate}% moms</span>
                      <span className="mx-2">•</span>
                      <span>{expense.expense_amount_excl_vat?.toFixed(2)} kr ex moms</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* General Report Images */}
          <div>
            <label className="form-label flex items-center gap-2">
              <Camera className="h-4 w-4" />
              Övriga bilder
            </label>
            <p className="text-xs text-nature-500 dark:text-nature-400 mb-3">
              Översiktsbilder eller bilder som inte hör till en specifik artikel
            </p>
            <div className="space-y-3">
              {/* Before images */}
              {reportImages.filter(img => img.type === 'before').length > 0 && (
                <div>
                  <p className="text-sm font-medium text-amber-700 dark:text-amber-300 mb-2">Före:</p>
                  <div className="flex flex-wrap gap-2">
                    {reportImages.filter(img => img.type === 'before').map((img, idx) => {
                      const actualIdx = reportImages.findIndex(i => i === img)
                      return (
                        <div key={idx} className="relative group">
                          <img
                            src={img.url}
                            alt={img.name}
                            className="w-20 h-20 object-cover rounded-lg border-2 border-amber-400 dark:border-amber-500"
                          />
                          {(!assignment.reportedAt || assignment.tgStatus === 'komplettering') && (
                            <button
                              onClick={() => removeGeneralImage(actualIdx)}
                              className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
              {/* After images */}
              {reportImages.filter(img => img.type === 'after').length > 0 && (
                <div>
                  <p className="text-sm font-medium text-green-700 dark:text-green-300 mb-2">Efter:</p>
                  <div className="flex flex-wrap gap-2">
                    {reportImages.filter(img => img.type === 'after').map((img, idx) => {
                      const actualIdx = reportImages.findIndex(i => i === img)
                      return (
                        <div key={idx} className="relative group">
                          <img
                            src={img.url}
                            alt={img.name}
                            className="w-20 h-20 object-cover rounded-lg border-2 border-green-400 dark:border-green-500"
                          />
                          {(!assignment.reportedAt || assignment.tgStatus === 'komplettering') && (
                            <button
                              onClick={() => removeGeneralImage(actualIdx)}
                              className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
              {/* Other images */}
              {reportImages.filter(img => img.type === 'other').length > 0 && (
                <div>
                  <p className="text-sm font-medium text-nature-600 dark:text-nature-300 mb-2">Övriga:</p>
                  <div className="flex flex-wrap gap-2">
                    {reportImages.filter(img => img.type === 'other').map((img, idx) => {
                      const actualIdx = reportImages.findIndex(i => i === img)
                      return (
                        <div key={idx} className="relative group">
                          <img
                            src={img.url}
                            alt={img.name}
                            className="w-20 h-20 object-cover rounded-lg border border-nature-300 dark:border-nature-500"
                          />
                          {(!assignment.reportedAt || assignment.tgStatus === 'komplettering') && (
                            <button
                              onClick={() => removeGeneralImage(actualIdx)}
                              className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
              {/* Hidden inputs */}
              <input
                ref={beforeGeneralInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => {
                  handleGeneralImageUpload(e.target.files, 'before')
                  e.target.value = ''
                }}
                className="hidden"
              />
              <input
                ref={afterGeneralInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => {
                  handleGeneralImageUpload(e.target.files, 'after')
                  e.target.value = ''
                }}
                className="hidden"
              />
              <input
                ref={otherGeneralInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => {
                  handleGeneralImageUpload(e.target.files, 'other')
                  e.target.value = ''
                }}
                className="hidden"
              />
              {/* Upload buttons - only show when not submitted or when supplement needed */}
              {(!assignment.reportedAt || assignment.tgStatus === 'komplettering') && (
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => beforeGeneralInputRef.current?.click()}
                    disabled={uploadingGeneral}
                    className="px-3 py-2 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-lg hover:bg-amber-200 dark:hover:bg-amber-900/50 flex items-center gap-2 text-sm"
                  >
                    {uploadingGeneral ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Camera className="h-4 w-4" />
                    )}
                    Före-bild
                  </button>
                  <button
                    onClick={() => afterGeneralInputRef.current?.click()}
                    disabled={uploadingGeneral}
                    className="px-3 py-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-lg hover:bg-green-200 dark:hover:bg-green-900/50 flex items-center gap-2 text-sm"
                  >
                    {uploadingGeneral ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Camera className="h-4 w-4" />
                    )}
                    Efter-bild
                  </button>
                  <button
                    onClick={() => otherGeneralInputRef.current?.click()}
                    disabled={uploadingGeneral}
                    className="px-3 py-2 bg-nature-100 dark:bg-nature-700 text-nature-700 dark:text-nature-300 rounded-lg hover:bg-nature-200 dark:hover:bg-nature-600 flex items-center gap-2 text-sm"
                  >
                    {uploadingGeneral ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Camera className="h-4 w-4" />
                    )}
                    Övrig bild
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="form-label">Övriga anteckningar</label>
            <p className="text-xs text-nature-500 dark:text-nature-400 mb-2">
              Här kan du rapportera in extra utlägg, material eller arbete som inte var planerat.
            </p>
            <textarea
              value={report}
              onChange={(e) => setReport(e.target.value)}
              disabled={!!assignment.reportedAt && assignment.tgStatus !== 'komplettering'}
              className="input-field disabled:bg-nature-100 dark:disabled:bg-nature-700 disabled:cursor-not-allowed"
              rows={4}
            />
          </div>

          {/* Draft indicator - only show when not yet submitted and not in komplettering mode */}
          {(assignment.isDraft || lastSavedAt) && !assignment.reportedAt && assignment.tgStatus !== 'komplettering' && (
            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <p className="text-sm text-amber-700 dark:text-amber-300">
                Detta är ett sparat utkast
                {lastSavedAt
                  ? ` (senast sparat ${format(lastSavedAt, 'HH:mm')})`
                  : assignment.draftSavedAt && ` (sparat ${format(new Date(assignment.draftSavedAt), 'yyyy-MM-dd HH:mm')}${assignment.draftSaverName ? ` av ${assignment.draftSaverName}` : ''})`
                }.
                {' '}Vill du uppdatera? Gör dina ändringar och spara igen, eller skicka in rapporten direkt.
              </p>
            </div>
          )}

          {/* Show different content based on status */}
          {assignment.tgStatus === 'komplettering' ? (
            /* Report needs supplement - show warning and allow editing */
            <>
              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800 dark:text-amber-200">
                      Komplettering begärd
                    </p>
                    <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                      Din rapport behöver kompletteras. Uppdatera informationen och skicka in igen.
                    </p>
                    {assignment.adminFeedback && (
                      <div className="mt-3 p-3 bg-white dark:bg-nature-700 rounded border border-amber-300 dark:border-amber-600">
                        <p className="text-xs font-medium text-amber-800 dark:text-amber-200 mb-1">Meddelande:</p>
                        <p className="text-sm text-nature-700 dark:text-nature-300">{assignment.adminFeedback.replace(/^\[(BEGÄRD|MANUELL)\]\s*/, '')}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Buttons for resubmission */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleSaveDraft}
                  disabled={saving || submitting || !hasUnsavedChanges}
                  className="btn-secondary flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  {saving ? 'Sparar...' : 'Spara utkast'}
                </button>

                <button
                  onClick={() => setShowSubmitConfirmation(true)}
                  disabled={saving || submitting || !hasReportContent}
                  className="btn-primary flex items-center justify-center gap-2"
                >
                  <Send className="h-4 w-4" />
                  Skicka komplettering
                </button>
              </div>
            </>
          ) : !assignment.reportedAt && (
            /* Report not submitted - show buttons */
            <>
              {/* Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleSaveDraft}
                  disabled={saving || submitting || !hasUnsavedChanges}
                  className="btn-secondary flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  {saving ? 'Sparar...' : 'Spara utkast'}
                </button>

                <button
                  onClick={() => setShowSubmitConfirmation(true)}
                  disabled={saving || submitting || !hasReportContent}
                  className="btn-primary flex items-center justify-center gap-2"
                >
                  <Send className="h-4 w-4" />
                  Skicka för godkännande
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Attachments Section */}
      <AttachmentSection assignmentId={id!} canUpload={false} defaultCollapsed={true} />

      {/* Comments Section */}
      <CommentSection
        entityType="assignment"
        entityId={id!}
        title="Kommentarer"
        defaultCollapsed={false}
      />

      {/* Decline Modal */}
      <TgDeclineModal
        isOpen={showDeclineModal}
        onClose={() => setShowDeclineModal(false)}
        subcontractorName={assignment.subcontractorName}
        assignmentTitle={assignment.title}
        assignmentDate={assignment.start_date ? format(new Date(assignment.start_date), 'd MMMM yyyy', { locale: sv }) : undefined}
        onConfirm={handleDecline}
      />

      {/* Submit Confirmation Modal */}
      {showSubmitConfirmation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-nature-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-full">
                <AlertTriangle className="h-6 w-6 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100">
                  Skicka rapport?
                </h3>
                <p className="text-sm text-nature-600 dark:text-nature-400 mt-1">
                  Detta är en slutgiltig handling. När rapporten är inskickad kan du inte längre göra ändringar själv.
                </p>
                <p className="text-sm text-nature-600 dark:text-nature-400 mt-2">
                  Om du behöver ändra något efter inskickning måste du kontakta Malin.
                </p>
              </div>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowSubmitConfirmation(false)}
                className="btn-secondary"
              >
                Avbryt
              </button>
              <button
                onClick={() => {
                  setShowSubmitConfirmation(false)
                  handleSubmitReport()
                }}
                disabled={submitting}
                className="btn-primary flex items-center gap-2"
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                {submitting ? 'Skickar...' : 'Ja, skicka rapport'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Expense Modal */}
      {showExpenseModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-nature-800 rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-full">
                {editingExpenseIndex !== null ? (
                  <Pencil className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                ) : (
                  <Receipt className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                )}
              </div>
              <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100">
                {editingExpenseIndex !== null ? 'Redigera utlägg' : 'Lägg till utlägg'}
              </h3>
            </div>

            <div className="space-y-4">
              {/* Description */}
              <div>
                <label className="form-label">Beskrivning *</label>
                <input
                  type="text"
                  value={newExpense.description}
                  onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })}
                  className="input-field"
                  placeholder="T.ex. Parkering, Material, Bränsle..."
                />
              </div>

              {/* VAT rate */}
              <div>
                <label className="form-label">Momssats</label>
                <select
                  value={newExpense.vatRate}
                  onChange={(e) => {
                    const newRate = Number(e.target.value)
                    if (newExpense.lastEdited === 'incl' && newExpense.amountInclVat) {
                      const incl = parseFloat(newExpense.amountInclVat.replace(',', '.'))
                      if (!isNaN(incl)) {
                        const excl = newRate > 0 ? incl / (1 + newRate / 100) : incl
                        setNewExpense({ ...newExpense, vatRate: newRate, amountExclVat: excl.toFixed(2) })
                        return
                      }
                    }
                    if (newExpense.amountExclVat) {
                      const excl = parseFloat(newExpense.amountExclVat.replace(',', '.'))
                      if (!isNaN(excl)) {
                        const incl = excl * (1 + newRate / 100)
                        setNewExpense({ ...newExpense, vatRate: newRate, amountInclVat: incl.toFixed(2) })
                        return
                      }
                    }
                    setNewExpense({ ...newExpense, vatRate: newRate })
                  }}
                  className="input-field"
                >
                  <option value={25}>25%</option>
                  <option value={12}>12%</option>
                  <option value={6}>6%</option>
                  <option value={0}>0% (momsfritt)</option>
                </select>
              </div>

              {/* Amount excl VAT */}
              <div>
                <label className="form-label">Belopp ex moms (kr) *</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={newExpense.amountExclVat}
                  onChange={(e) => {
                    const val = e.target.value
                    const parsed = parseFloat(val.replace(',', '.'))
                    const inclVal = !isNaN(parsed) && val ? (parsed * (1 + newExpense.vatRate / 100)).toFixed(2) : ''
                    setNewExpense({ ...newExpense, amountExclVat: val, amountInclVat: inclVal, lastEdited: 'excl' })
                  }}
                  className="input-field"
                  placeholder="0.00"
                />
              </div>

              {/* Amount incl VAT */}
              <div>
                <label className="form-label">Belopp inkl moms (kr)</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={newExpense.amountInclVat}
                  onChange={(e) => {
                    const val = e.target.value
                    const parsed = parseFloat(val.replace(',', '.'))
                    const exclVal = !isNaN(parsed) && val ? (parsed / (1 + newExpense.vatRate / 100)).toFixed(2) : ''
                    setNewExpense({ ...newExpense, amountInclVat: val, amountExclVat: exclVal, lastEdited: 'incl' })
                  }}
                  className="input-field"
                  placeholder="0.00"
                />
              </div>

              {/* Receipt images */}
              <div>
                <label className="form-label">Kvittobild (valfritt)</label>
                <div className="space-y-2">
                  {/* Show uploaded images */}
                  {newExpense.images.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {newExpense.images.map((img, idx) => (
                        <div key={idx} className="relative group">
                          <img
                            src={img.url}
                            alt={img.name}
                            className="w-16 h-16 object-cover rounded border border-amber-300 dark:border-amber-600"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setNewExpense(prev => ({
                                ...prev,
                                images: prev.images.filter((_, i) => i !== idx)
                              }))
                            }}
                            className="absolute -top-1 -right-1 p-0.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Upload button */}
                  <button
                    type="button"
                    onClick={() => expenseImageInputRef.current?.click()}
                    disabled={uploadingExpenseImage}
                    className="flex items-center gap-2 px-3 py-2 text-sm bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors"
                  >
                    {uploadingExpenseImage ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Camera className="h-4 w-4" />
                    )}
                    {uploadingExpenseImage ? 'Laddar upp...' : 'Lägg till kvittobild'}
                  </button>
                  <input
                    ref={expenseImageInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => {
                      handleExpenseImageUpload(e.target.files)
                      e.target.value = ''
                    }}
                    className="hidden"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 justify-end mt-6">
              <button
                onClick={() => {
                  setShowExpenseModal(false)
                  setEditingExpenseIndex(null)
                  setNewExpense({ description: '', amountExclVat: '', amountInclVat: '', vatRate: 25, lastEdited: 'excl', images: [] })
                }}
                className="btn-secondary"
              >
                Avbryt
              </button>
              <button
                onClick={() => {
                  if (!newExpense.description.trim()) {
                    toast.error('Ange en beskrivning')
                    return
                  }
                  const amountExclVat = parseFloat(newExpense.amountExclVat.replace(',', '.'))
                  if (isNaN(amountExclVat) || amountExclVat <= 0) {
                    toast.error('Ange ett giltigt belopp')
                    return
                  }

                  const amountInclVat = amountExclVat * (1 + newExpense.vatRate / 100)

                  const expense: ReportedArticle = {
                    article_id: null,
                    article_name: newExpense.description.trim(),
                    planned_quantity: null,
                    actual_quantity: 1,
                    unit: 'st',
                    notes: '',
                    is_extra: false,
                    images: newExpense.images,
                    is_expense: true,
                    expense_amount_incl_vat: Math.round(amountInclVat * 100) / 100,
                    expense_amount_excl_vat: amountExclVat,
                    expense_vat_rate: newExpense.vatRate
                  }

                  if (editingExpenseIndex !== null) {
                    // Update existing expense
                    const updatedExpenses = [...expenses]
                    updatedExpenses[editingExpenseIndex] = expense
                    setExpenses(updatedExpenses)
                    toast.success('Utlägg uppdaterat')
                  } else {
                    // Add new expense
                    setExpenses([...expenses, expense])
                    toast.success('Utlägg tillagt')
                  }
                  setShowExpenseModal(false)
                  setEditingExpenseIndex(null)
                  setNewExpense({ description: '', amountExclVat: '', amountInclVat: '', vatRate: 25, lastEdited: 'excl', images: [] })
                }}
                className="btn-primary flex items-center gap-2"
              >
                {editingExpenseIndex !== null ? (
                  <>
                    <CheckCircle className="h-4 w-4" />
                    Spara
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4" />
                    Lägg till
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Coworker Popup */}
      {selectedCoworker && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          onClick={() => setSelectedCoworker(null)}
        >
          <div className="absolute inset-0 bg-black/30" />
          <div
            className="relative bg-white dark:bg-nature-800 rounded-lg shadow-xl p-4 min-w-[200px]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-3">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium"
                style={{ backgroundColor: selectedCoworker.color || '#6B7280' }}
              >
                {selectedCoworker.name.charAt(0)}
              </div>
              <div>
                <p className="font-medium text-nature-900 dark:text-nature-100">
                  {selectedCoworker.name}
                </p>
              </div>
            </div>
            {selectedCoworker.phone ? (
              <a
                href={`tel:${selectedCoworker.phone}`}
                className="flex items-center gap-2 text-primary-600 dark:text-primary-400 hover:underline"
              >
                <Phone className="h-4 w-4" />
                {selectedCoworker.phone}
              </a>
            ) : (
              <p className="text-sm text-nature-500 dark:text-nature-400">
                Inget telefonnummer registrerat
              </p>
            )}
            <button
              onClick={() => setSelectedCoworker(null)}
              className="absolute top-2 right-2 p-1 text-nature-400 hover:text-nature-600 dark:hover:text-nature-200"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
